import React from 'react'
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ClearIcon from '@mui/icons-material/Clear'
import { GANTT_VIEW_MODE } from '../helpers/ganttDates'

const pillButtonSx = {
  textTransform: 'none',
  borderRadius: 999,
  px: 2,
  height: 36,
  fontWeight: 500,
  boxShadow: 'none',
}

export default function GanttToolbar({
  searchValue,
  onSearchChange,
  onClearSearch,
  projectStatus,
  onProjectStatusChange,
  onExpandAll,
  onCollapseAll,
  viewMode,
  onViewModeChange,
  visibleRangeLabel,
  onPrevRange,
  onNextRange,
}) {
  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        gap={2}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#3A3A3A' }}>
          Gantt Chart
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={onPrevRange}>
            <KeyboardArrowLeftIcon />
          </IconButton>
          <Typography sx={{ minWidth: 120, textAlign: 'center', fontWeight: 500 }}>
            {visibleRangeLabel}
          </Typography>
          <IconButton onClick={onNextRange}>
            <KeyboardArrowRightIcon />
          </IconButton>
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        alignItems={{ xs: 'stretch', lg: 'center' }}
        justifyContent="space-between"
        gap={2}
      >
        <TextField
          size="small"
          fullWidth
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search for a project, task or user..."
          sx={{
            maxWidth: 500,
            outline: 'none !important',
            '& .MuiOutlinedInput-root': {
              outline: 'none !important',
              borderRadius: 999,
              color: '#E5E5E5',
              borderColor: '#e5e5e5',
              backgroundColor: '#FFFFFF',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.9998 14.0008L11.1064 11.1074" stroke="#8A8A8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#8A8A8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </InputAdornment>
            ),
            endAdornment: searchValue ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <TextField
            select
            size="small"
            value={projectStatus}
            onChange={(event) => onProjectStatusChange(event.target.value)}
            SelectProps={{
              IconComponent: KeyboardArrowDownIcon,
            }}
            sx={{
              minWidth: 110,
              '& .MuiOutlinedInput-root': {
                outline: 'none !important',
                backgroundColor: '#ffffff',
                borderRadius: 999,
                height: 36,
                color: '#3a3a3a',
                borderColor: '#3a3a3a',
                '&:hover': {
                  borderColor: '#deecff',
                  backgroundColor: '#deecff',
                },
              },
              '& .MuiSelect-icon': {
                color: '#3a3a3a',
              }
            }}
          >
            <MenuItem value="ongoing">Ongoing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
          <Button 
            variant="outlined" 
            onClick={onExpandAll} 
            sx={{
              ...pillButtonSx,
              color: '#2666BE',
              borderColor: '#2666BE',
              '&:hover': {
                borderColor: '#DEECFF',
                backgroundColor: '#DEECFF',
              },
            }}
            startIcon={<KeyboardDoubleArrowDownIcon />}
            >
            Expand All
          </Button>
          <Button 
            variant="outlined" 
            onClick={onCollapseAll} 
            sx={{
              ...pillButtonSx,
              color: '#2666BE',
              borderColor: '#2666BE',
              '&:hover': {
                borderColor: '#deecff',
                backgroundColor: '#deecff',
              },
            }}
            startIcon={<KeyboardDoubleArrowUpIcon />}
            >
            Collapse All
          </Button>
          <TextField
            select
            size="small"
            value={viewMode}
            onChange={(event) => onViewModeChange(event.target.value)}
            SelectProps={{
              IconComponent: KeyboardArrowDownIcon,
            }}
            sx={{
              minWidth: 110,
              '& .MuiOutlinedInput-root': {
                outline: 'none !important',
                backgroundColor: '#ffffff',
                borderRadius: 999,
                height: 36,
                color: '#3a3a3a',
                borderColor: '#3a3a3a',
                '&:hover': {
                  borderColor: '#deecff',
                  backgroundColor: '#deecff',
                },
              },
              '& .MuiSelect-icon': {
                color: '#3a3a3a',
              }
            }}
          >
            <MenuItem value={GANTT_VIEW_MODE.MONTH}>Month</MenuItem>
            <MenuItem value={GANTT_VIEW_MODE.WEEK}>Weekly</MenuItem>
          </TextField>
        </Stack>
      </Stack>
    </Stack>
  )
}
